import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Cog6ToothIcon, 
  UsersIcon, 
  // LanguageIcon, // Removed unused import
  BellIcon, 
  ShieldCheckIcon,
  // RocketLaunchIcon, // Removed unused import
  DocumentIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface CompanySettings {
  id?: number;
  name: string;
  address: string;
  phone: string;
  email: string;
  website?: string;
  logoUrl?: string;
  taxId?: string; // БИН
  director?: string; // Директор
  basisOfAuthority?: string; // Основание полномочий (Устав)
  bankAccount?: string; // ИИК
  bankName?: string; // Банк
  bankBik?: string; // БИК
  // -- Comment out fields that don't exist in the model/API or are handled elsewhere --
  // language?: string;
  // dateFormat?: string;
  // timezone?: string;
  // currency?: string;
}

interface NotificationSettings {
  emailNotifications: boolean;
  smsNotifications: boolean;
  orderCreated: boolean;
  orderStatusChanged: boolean;
  contractCreated: boolean;
  paymentReceived: boolean;
  workOrderAssigned: boolean;
  lowAvailability: boolean;
}

const Settings: React.FC = () => {
  const { user, can } = useAuth(); // Changed isAllowed to can
  const [loading, setLoading] = useState(true);
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  
  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    name: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    logoUrl: '',
    taxId: '',
    director: '',
    basisOfAuthority: '',
    bankAccount: '',
    bankName: '',
    bankBik: ''
    // -- Remove commented out fields from default values --
    // language: 'ru',
    // dateFormat: 'DD.MM.YYYY', 
    // timezone: 'Asia/Almaty', 
    // currency: 'KZT' 
  });
  
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    emailNotifications: true,
    smsNotifications: false,
    orderCreated: true,
    orderStatusChanged: true,
    contractCreated: true,
    paymentReceived: true,
    workOrderAssigned: true,
    lowAvailability: false
  });
  
  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        // Fetch company settings
        const companyResponse = await axios.get('/settings/company');
        setCompanySettings(companyResponse.data);
        
        // Fetch notification settings
        const notificationResponse = await axios.get('/settings/notifications');
        setNotificationSettings(notificationResponse.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, []);
  
  const handleCompanyChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCompanySettings(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleNotificationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setNotificationSettings(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  
  const saveCompanySettings = async () => {
    setSavingCompany(true);
    try {
      await axios.put('/settings/company', companySettings);
      // Success notification could be added here
    } catch (error) {
      console.error('Error saving company settings:', error);
    } finally {
      setSavingCompany(false);
    }
  };
  
  const saveNotificationSettings = async () => {
    setSavingNotifications(true);
    try {
      await axios.put('/settings/notifications', notificationSettings);
      // Success notification could be added here
    } catch (error) {
      console.error('Error saving notification settings:', error);
    } finally {
      setSavingNotifications(false);
    }
  };
  
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('logo', file);
    
    try {
      const response = await axios.post('/settings/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setCompanySettings(prev => ({
        ...prev,
        logoUrl: response.data.logoUrl
      }));
      
      // Success notification could be added here
    } catch (error) {
      console.error('Error uploading logo:', error);
    }
  };
  
  const runBackup = async () => {
    try {
      const response = await axios.post('/settings/backup', {}, {
        responseType: 'blob'
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `signage_agency_backup_${date}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error creating backup:', error);
    }
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      <PageHeader
        title="Настройки"
        subtitle="Управление настройками системы"
      />
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Settings Navigation */}
        <div className="space-y-4">
          {/* Settings Menu */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">Меню настроек</h3>
            </div>
            <div className="p-2">
              <a href="#company" className="flex items-center px-4 py-2 rounded-md hover:bg-gray-50">
                <Cog6ToothIcon className="h-5 w-5 text-gray-500 mr-2" />
                <span className="text-gray-700">Компания</span>
              </a>
              {can('settings.user_management') && ( // Changed from isAllowed
                <Link to="/settings/users" className="flex items-center px-4 py-2 rounded-md hover:bg-gray-50">
                  <UsersIcon className="h-5 w-5 text-gray-500 mr-2" />
                  <span className="text-gray-700">Пользователи</span>
                </Link>
              )}
              <a href="#notifications" className="flex items-center px-4 py-2 rounded-md hover:bg-gray-50">
                <BellIcon className="h-5 w-5 text-gray-500 mr-2" />
                <span className="text-gray-700">Уведомления</span>
              </a>
              {can('settings.backup') && ( // Changed from isAllowed
                <a href="#system" className="flex items-center px-4 py-2 rounded-md hover:bg-gray-50">
                  <ShieldCheckIcon className="h-5 w-5 text-gray-500 mr-2" />
                  <span className="text-gray-700">Система</span>
                </a>
              )}
            </div>
          </div>
          
          {/* User Info */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center space-x-3">
              <div className="bg-primary text-white p-3 rounded-full">
                <UsersIcon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">{user?.name || user?.username}</h3>
                <p className="text-sm text-gray-500">{user?.role}</p>
              </div>
            </div>
          </div>
          
          {/* Backup & Restore */}
          {can('settings.backup') && ( // Changed from isAllowed
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Резервное копирование</h3>
              <button
                type="button"
                className="w-full btn-secondary flex items-center justify-center"
                onClick={runBackup}
              >
                <ArrowPathIcon className="h-5 w-5 mr-1" />
                Создать резервную копию
              </button>
            </div>
          )}
        </div>
        
        {/* Settings Content */}
        <div className="md:col-span-2 space-y-6">
          {/* Company Settings */}
          <div id="company" className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Настройки компании</h3>
              <Cog6ToothIcon className="h-5 w-5 text-gray-500" />
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Название компании
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={companySettings.name}
                    onChange={handleCompanyChange}
                    className="form-input block w-full mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="taxId" className="block text-sm font-medium text-gray-700">
                    БИН/ИИН
                  </label>
                  <input
                    type="text"
                    id="taxId"
                    name="taxId"
                    value={companySettings.taxId}
                    onChange={handleCompanyChange}
                    className="form-input block w-full mt-1"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                  Адрес
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={companySettings.address}
                  onChange={handleCompanyChange}
                  className="form-input block w-full mt-1"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div>
                   <label htmlFor="director" className="block text-sm font-medium text-gray-700">
                     Директор
                   </label>
                   <input
                     type="text"
                     id="director"
                     name="director"
                     value={companySettings.director || ''}
                     onChange={handleCompanyChange}
                     className="form-input block w-full mt-1"
                   />
                 </div>
                 <div>
                   <label htmlFor="basisOfAuthority" className="block text-sm font-medium text-gray-700">
                     Действует на основании
                   </label>
                   <input
                     type="text"
                     id="basisOfAuthority"
                     name="basisOfAuthority"
                     value={companySettings.basisOfAuthority || ''}
                     onChange={handleCompanyChange}
                     className="form-input block w-full mt-1"
                   />
                 </div>
               </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Телефон
                  </label>
                  <input
                    type="text"
                    id="phone"
                    name="phone"
                    value={companySettings.phone}
                    onChange={handleCompanyChange}
                    className="form-input block w-full mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={companySettings.email}
                    onChange={handleCompanyChange}
                    className="form-input block w-full mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                 <div>
                   <label htmlFor="bankAccount" className="block text-sm font-medium text-gray-700">
                     ИИК
                   </label>
                   <input
                     type="text"
                     id="bankAccount"
                     name="bankAccount"
                     value={companySettings.bankAccount || ''}
                     onChange={handleCompanyChange}
                     className="form-input block w-full mt-1"
                   />
                 </div>
                 <div>
                   <label htmlFor="bankName" className="block text-sm font-medium text-gray-700">
                     Банк
                   </label>
                   <input
                     type="text"
                     id="bankName"
                     name="bankName"
                     value={companySettings.bankName || ''}
                     onChange={handleCompanyChange}
                     className="form-input block w-full mt-1"
                   />
                 </div>
                 <div>
                   <label htmlFor="bankBik" className="block text-sm font-medium text-gray-700">
                     БИК
                   </label>
                   <input
                     type="text"
                     id="bankBik"
                     name="bankBik"
                     value={companySettings.bankBik || ''}
                     onChange={handleCompanyChange}
                     className="form-input block w-full mt-1"
                   />
                 </div>
               </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Логотип компании
                </label>
                <div className="flex items-center space-x-4">
                  <div className="border rounded-lg p-2 w-24 h-24 flex items-center justify-center">
                    {companySettings.logoUrl ? (
                      <img 
                        src={companySettings.logoUrl} 
                        alt="Company Logo" 
                        className="max-h-full max-w-full"
                      />
                    ) : (
                      <DocumentIcon className="h-10 w-10 text-gray-300" />
                    )}
                  </div>
                  <div className="flex-1">
                    <label 
                      htmlFor="logo-upload" 
                      className="btn-secondary inline-block cursor-pointer"
                    >
                      Загрузить логотип
                    </label>
                    <input 
                      id="logo-upload" 
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      onChange={handleLogoUpload}
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Рекомендуемый размер: 200x200 пикселей
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t">
              <button
                type="button"
                className="btn-primary"
                onClick={saveCompanySettings}
                disabled={savingCompany}
              >
                {savingCompany ? 'Сохранение...' : 'Сохранить настройки'}
              </button>
            </div>
          </div>
          
          {/* Notification Settings */}
          <div id="notifications" className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Настройки уведомлений</h3>
              <BellIcon className="h-5 w-5 text-gray-500" />
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="emailNotifications"
                    name="emailNotifications"
                    checked={notificationSettings.emailNotifications}
                    onChange={handleNotificationChange}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="emailNotifications" className="ml-2 block text-sm text-gray-700">
                    Email уведомления
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="smsNotifications"
                    name="smsNotifications"
                    checked={notificationSettings.smsNotifications}
                    onChange={handleNotificationChange}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="smsNotifications" className="ml-2 block text-sm text-gray-700">
                    SMS уведомления
                  </label>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Уведомлять меня о:</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="orderCreated"
                      name="orderCreated"
                      checked={notificationSettings.orderCreated}
                      onChange={handleNotificationChange}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="orderCreated" className="ml-2 block text-sm text-gray-700">
                      Новый заказ
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="orderStatusChanged"
                      name="orderStatusChanged"
                      checked={notificationSettings.orderStatusChanged}
                      onChange={handleNotificationChange}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="orderStatusChanged" className="ml-2 block text-sm text-gray-700">
                      Изменение статуса заказа
                    </label>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="contractCreated"
                      name="contractCreated"
                      checked={notificationSettings.contractCreated}
                      onChange={handleNotificationChange}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="contractCreated" className="ml-2 block text-sm text-gray-700">
                      Новый договор
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="paymentReceived"
                      name="paymentReceived"
                      checked={notificationSettings.paymentReceived}
                      onChange={handleNotificationChange}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="paymentReceived" className="ml-2 block text-sm text-gray-700">
                      Получена оплата
                    </label>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="workOrderAssigned"
                      name="workOrderAssigned"
                      checked={notificationSettings.workOrderAssigned}
                      onChange={handleNotificationChange}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="workOrderAssigned" className="ml-2 block text-sm text-gray-700">
                      Назначен наряд
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="lowAvailability"
                      name="lowAvailability"
                      checked={notificationSettings.lowAvailability}
                      onChange={handleNotificationChange}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="lowAvailability" className="ml-2 block text-sm text-gray-700">
                      Низкая доступность конструкций
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t">
              <button
                type="button"
                className="btn-primary"
                onClick={saveNotificationSettings}
                disabled={savingNotifications}
              >
                {savingNotifications ? 'Сохранение...' : 'Сохранить настройки'}
              </button>
            </div>
          </div>
          
          {/* System Settings */}
          {can('settings.system.view') && ( // Changed from isAllowed, new permission
            <div id="system" className="bg-white rounded-lg shadow">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Системные настройки</h3>
                <ShieldCheckIcon className="h-5 w-5 text-gray-500" />
              </div>
              <div className="p-4">
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Информация о системе</h4>
                  <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
                    <p><span className="font-medium">Версия:</span> 1.0.0</p>
                    <p><span className="font-medium">База данных:</span> SQLite</p>
                    <p><span className="font-medium">Последнее обновление:</span> 04.04.2025</p>
                    <p><span className="font-medium">Среда:</span> Production</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between border-t pt-4 items-center">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Резервное копирование</h4>
                      <p className="text-xs text-gray-500">Создание резервной копии базы данных</p>
                    </div>
                    <button 
                      className="btn-secondary"
                      onClick={runBackup}
                    >
                      Создать копию
                    </button>
                  </div>
                  
                  <div className="flex justify-between border-t pt-4 items-center">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Оптимизация базы данных</h4>
                      <p className="text-xs text-gray-500">Оптимизация и очистка базы данных</p>
                    </div>
                    <button className="btn-secondary">
                      Оптимизировать
                    </button>
                  </div>
                  
                  <div className="flex justify-between border-t pt-4 items-center">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Очистка логов</h4>
                      <p className="text-xs text-gray-500">Удаление старых файлов логов</p>
                    </div>
                    <button className="btn-secondary">
                      Очистить
                    </button>
                  </div>
                  
                  <div className="flex justify-between border-t pt-4 items-center">
                    <div>
                      <h4 className="text-sm font-medium text-red-700">Сброс системы</h4>
                      <p className="text-xs text-gray-500">Возврат к заводским настройкам</p>
                    </div>
                    <button className="btn-danger">
                      Сбросить
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
